import { UIIcon } from './config/icons';
import { aInitTrack } from './lib/mdl/a-track';
import { c_producers, cProducerGetAutofill } from './lib/mdl/c-producer';
import { FRoute } from './lib/mdl/f-route';
import { haveRoleInAnyProducer, haveRoleInProducer, haveRoleInProducers } from './lib/me/can-producer';
import { meAutoLogin, meLogout, the_login_scope, the_reset_token, the_user } from './lib/me/login';
import { meSetCurrentProducer, meSetDefaultProducer, the_producer } from './lib/me/producer';
import { UIButton } from './lib/ui/component/button';
import { uiBuildNavigationMenu } from './lib/ui/component/navigation-menu';
import { UISelect } from './lib/ui/component/select';
import { showWarning } from './lib/ui/component/warning';
import { $composer } from './lib/ui/composer';
import { $el } from './lib/ui/elements';
import { $recaptcha } from './lib/ui/media/recaptcha';
import { UIRelPos } from './lib/ui/types';
import { $analytics } from './lib/y/analytics';
import { $log, xavis_experiments } from './lib/y/debug';
import { $on } from './lib/y/events';
import { $i18n } from './lib/y/i18n';
import { $location, YLocation, YPushSpec, YRoute } from './lib/y/location';
import { $storage } from './lib/y/storage';
import { y_ready } from './lib/y/util';
import { ProducerAccountScreen } from './producer/account';
import { ProducerBookingsScreen } from './producer/bookings';
import { ProducerCustomScreen } from './producer/custom';
import { ProducerEventScreen, ProducerEventsScreen } from './producer/events';
import { ProducerInvoicesScreen } from './producer/invoices';
import { producerShowLogin } from './producer/login';
import { ProducerReviewsScreen } from './producer/reviews';
import { ProducerTeamScreen } from './producer/team';

$location.title_suffix = 'Abundo Partners';

if(!xavis_experiments)
    $analytics.initSegment('ZcUTuthbUJnSoUsOiavDHmO8LUAT1gMF');

$recaptcha.init();

function showPermissionWarning(permission: string) {
    let producer = '';
    if(the_user) {
        const producers = haveRoleInProducers('any');
        if(producers.length == 1) {
            const p = c_producers.maybe(producers[0]);
            if(p)
                producer = p.name + ' ';
        }
    }
    showWarning({ text: 'Du har inte behörighet till ' + permission + '. Vänligen kontakta ' + producer + ' administratör för att få tillgång', cancel_text: null });
}

function calcRoute(r: FRoute): YRoute | undefined {
    const path = r.path.split('/').filter(c => c);
    let match: ((l: YLocation | YPushSpec) => boolean) | undefined;

    try {
        const match_flags = Object.keys(r.match_flags);
        if(match_flags.length) {
            match = () => {
                for(const f of match_flags) {
                    if(f == 'logged_in' && !the_login_scope)
                        return false;
                    if(f == 'logged_out' && the_login_scope)
                        return false;
                    if(f == 'xavi' && !xavis_experiments)
                        return false;
                }

                return true;
            }
        }

        switch(r.target_type) {
            case 'page':
                return { path: path, match: match, screen: () => new ProducerCustomScreen(r.target_page_id!) };
            case 'client_redirect':
                return { path: path, match: match, redirect: r.target_url?.split('/').filter(c => c) };
        }
    } catch {}

    $log('app', 'can\'t process route', r);

    return;
}

const query = $location.getCurrentQuery();

const menu_routes = uiBuildNavigationMenu([
    { widget: () => $el.DIV('ui-logo i-menu-item') },
    {
        widget: () => {
            document.body.classList.add('tl-producer-colors');

            const producer_values = haveRoleInProducers('any').map(p => {
                const producer = c_producers.maybe(p)!;
                return { key: producer.id, name: producer.name };
            });
            const producer_select = new UISelect(producer_values, { kind: 'k-subtle', classes: 'm-harder' });
            producer_select.value = the_producer!.id;

            const add_event = new UIButton({
                left_icon: UIIcon.PLUS, classes: 'ui-tint-reverse', title: $i18n.pr.menu_new_event, on_click: () => {
                    $analytics.track('Event creation started', { duplicated: false });
                    return cProducerGetAutofill(the_producer!.id).then(autofill => {
                        $location.push({ transient: new ProducerEventScreen({ autofill: autofill }) });
                    });
                }
            });

            if(!haveRoleInProducer('events', the_producer!.id)) {
                add_event.ui_hide();
            }

            $on(producer_select, 'value', value => {
                if(!value) return
                const producer = c_producers.maybe(value);
                if(producer) {
                    $storage.putString('last_producer_id', producer.id, true);
                    meSetCurrentProducer(producer);
                    add_event.ui_show(undefined, !haveRoleInProducer('events', producer.id));
                }
            });

            return $el.DIV('tl-menu-producer i-menu-item', [
                $el.DIV('r-title', $i18n.pr.menu_logged_in_as),
                producer_select,
                add_event
            ]);
        }
    },
    { space: 1 },
    // { path: 'dashboard', icon: UIIcon.HOUSE, title: $i18n.pr.menu_dashboard, screen: () => new producer_dashboard.DashboardScreen},
    { path: 'bookings', icon: UIIcon.CALENDAR_DAYS, title: () => $i18n.pr.menu_upcoming, screen: () => new ProducerBookingsScreen, when: () => haveRoleInAnyProducer('bookings') },
    { path: 'events', icon: UIIcon.TICKET_SIMPLE, title: () => $i18n.pr.menu_events, screen: () => new ProducerEventsScreen, when: () => haveRoleInAnyProducer('events') },
    { path: 'reviews', icon: UIIcon.QUOTE_LEFT, title: () => $i18n.pr.menu_reviews, screen: () => new ProducerReviewsScreen, when: () => haveRoleInAnyProducer('bookings') },
    { path: 'team', icon: UIIcon.USERS, title: () => $i18n.pr.menu_team, screen: () => new ProducerTeamScreen, when: () => haveRoleInAnyProducer('admin') },
    { icon: UIIcon.USERS, title: () => $i18n.pr.menu_team, action: () => { showPermissionWarning('Team') }, when: () => !haveRoleInAnyProducer('admin') && (haveRoleInAnyProducer('economy') || haveRoleInAnyProducer('events')) },
    // { path: 'imported', title: $i18n.aa.m_imported, screen: () => new producer_imported.EventsScreen, when: () => haveProducerWithImportEvents() && haveRoleInAnyProducer('events') },
    { path: 'payments', icon: UIIcon.EURO_SIGN, title: () => $i18n.pr.menu_payments, screen: () => new ProducerInvoicesScreen, when: () => haveRoleInAnyProducer('economy') },
    { icon: UIIcon.EURO_SIGN, title: $i18n.pr.menu_payments, action: () => { showPermissionWarning('Sammanställningar') }, when: () => !haveRoleInAnyProducer('economy') && haveRoleInAnyProducer('events') },
    { path: 'account', icon: UIIcon.GEAR, title: () => $i18n.pr.menu_account, screen: m => new ProducerAccountScreen(m.tab as string) },
    // { path: 'sales', title: $i18n.aa.m_sales, screen: () => new producer_purchases.PurchasesScreen, when: () => !!xavis_experiments && haveRoleInAnyProducer('bookings') && haveProducerWithPPVEvents() },
    {
        icon: UIIcon.RIGHT_FROM_BRACKET, title: () => $i18n.aa.m_logout, action: () => {
            meLogout();
            $location.navigate([]);
        }
    }
], { position: UIRelPos.Left })[1];

const routes: YRoute[] = []

for(const r of window.ab_routes) {
    const route = calcRoute(r);
    route && routes.push(route);
}

routes.push(
    {
        match: () => !the_login_scope,
        redirect: [],
    },
    ...menu_routes,
    {
        match: () => !!the_login_scope,
        path: [],
        redirect: () => (haveRoleInAnyProducer('events') || haveRoleInAnyProducer('bookings')) ? ['bookings'] : ['account']
    },
    {
        redirect: []
    }
);

aInitTrack(false);

const login_response = meAutoLogin('producer', { role_invite: window.ab_producer_invite && (window.ab_producer_invite.id + window.ab_producer_invite.token) }, query.as_user, query.reset);

y_ready.then(() => {
    login_response.then(() => {
        $log('app', 'got response');
        if(!the_producer) {
            $log('app', 'setting producer');
            meSetDefaultProducer();
            $log('app', 'producer set', the_producer);
        }

        $location.init(routes, '/', $composer);
        if(the_reset_token)
            producerShowLogin('password_reset');
        else if(window.ab_producer_invite)
            producerShowLogin('producer_invite');

        // for(const p of c_producers.all) {
        //     if(haveRoleInProducer('economy', p.model.id)) {
        //         if(!p.valid_payment_account || !p.model.org_name || !p.model.org_number) {
        //             $composer.addBanner($el.B([$i18n.aa.w_missing_info, ' ', new UIButton({ title: $i18n.aa.b_missing_info, kind: 'k-text', classes: 'm-inline m-underline', on_click: () => {
        //                 $location.navigate(['account'], { tab: 'economy'});
        //                 $composer.removeBanner();
        //             }})]));

        //             break;
        //         }
        //     }
        // }
    });
});
